import { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { TypeStyleBodySmall, untilMd } from '@syconium/little-miss-figgy'

import { EmbroideryTypeEnum } from '../../../../__generated__/graphql/catalog/graphql'
import { useFeatureFlag } from '../../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { usePortal } from '../../../../app/_providers/PortalProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../containers/EmbroideryService'
import { EmbroideryModalService } from '../../../../containers/EmbroideryService/EmbroideryModalService'
import { getFormKind } from '../../../../containers/EmbroideryService/utils'
import { intlPrettyCurrency } from '../../../../lib/utils'
import { Embroidery } from '../../../../types/graphql'
import { LogoSneakPeek } from '../../EmbroiderySneakPeek/LogoSneakPeek'
import { normalizeDigitizationSettingToEmbroideryKind } from '../../forms/DTCLogoEmbroideryForm/utils'

import { EmbroideryOption } from './EmbroideryOption'

export const EmbroideryOptionList: FC<{
  embroideryOptions: ReadonlyArray<Embroidery>
}> = ({ embroideryOptions }) => {
  const { hasMandatoryLogoEmbroidery, hasMandatoryTextEmbroidery } = usePortal()

  const { formErrors, isPortalColorSelected } = EmbroideryModalService.useContainer()

  const {
    selectedEmbroideryFormOptions,
    setSelectedFormKind,
    defaultSelectedEmbroideryOptions,
    selectedEmbroideryOptions,
    productColorHex,
    productCategory,
  } = EmbroideryService.useContainer()

  const { locale, region } = useLocalization()
  const {
    embroidery: {
      modal: strings,
      validation,
      embroideryOptionTitles,
      from: logoPricePrefix,
      add,
      edit,
    },
  } = useTranslation()

  const dtcLogoOption = embroideryOptions.find(option => option.kind === 'LOGO_SETUP')
  const logoEmbroideryFlag = useFeatureFlag({
    key: 'web-dtc-logo-embroidery-rollout',
    defaultVariant: 'Control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-defaultVariant',
    skip: !dtcLogoOption,
  })

  const validEmbroideryOptions = useMemo(() => {
    const options = embroideryOptions.filter(option =>
      ['LOGO_ONLY', 'TEXT_ONLY', 'ICON_ONLY'].includes(option.kind)
    )
    if (logoEmbroideryFlag === 'show-add-logo' && dtcLogoOption) {
      options.push(dtcLogoOption)
    }
    return options
  }, [embroideryOptions, logoEmbroideryFlag, dtcLogoOption])

  const newEmbroideryOptions = ['LOGO_SETUP'] // price prefixes and badges should at some point move to contentful

  const selectedOptions = useMemo(() => {
    return {
      isLogoSelected:
        !!selectedEmbroideryOptions.LOGO_SETUP || !!selectedEmbroideryOptions.ADD_LOGO,
      isIconSelected: !!selectedEmbroideryOptions.ICON_ONLY,
    }
  }, [selectedEmbroideryOptions])

  const disabledText: Partial<Record<EmbroideryTypeEnum, string>> = {
    ICON_ONLY: strings.iconUnavailable,
    LOGO_SETUP: strings.logoUnavailable,
  }

  const getFormattedPrice = useCallback(
    (option: Embroidery) => {
      let logoPrice = 0

      if (option.kind === 'LOGO_SETUP') {
        // If no logo is selected yet, show the lowest default price (from +$20)
        if (!selectedEmbroideryFormOptions.logoFile) {
          logoPrice =
            embroideryOptions.find(option => option.kind === 'LOGO_BLACK_WHITE')?.variants[0]
              ?.price ?? 0
        } else {
          // If setup file exists, include its price in the total price ($60)
          if (selectedEmbroideryFormOptions.setupFile) {
            logoPrice += option?.variants[0]?.price ?? 0
          }
          // Add price for the selected color option
          const logoColorPrice =
            embroideryOptions.find(
              option =>
                option.kind ===
                normalizeDigitizationSettingToEmbroideryKind(
                  selectedEmbroideryFormOptions.logoColor
                )
            )?.variants[0]?.price ?? 0

          logoPrice += logoColorPrice
        }
      } else {
        // For other embroidery options, use their own price
        logoPrice = option?.variants[0]?.price ?? 0
      }

      return logoPrice > 0
        ? intlPrettyCurrency({
            cents: logoPrice,
            region: region,
            locale: locale,
          })
        : undefined
    },
    [locale, region, selectedEmbroideryFormOptions, embroideryOptions]
  )

  const hasTextPrefix = useCallback(
    (kind: EmbroideryTypeEnum) => {
      if (
        kind === 'LOGO_SETUP' &&
        !selectedEmbroideryFormOptions.setupFile &&
        !selectedEmbroideryFormOptions.logoFile
      ) {
        return <>{logoPricePrefix}</>
      }
      return null
    },
    [
      selectedEmbroideryFormOptions.setupFile,
      logoPricePrefix,
      selectedEmbroideryFormOptions.logoFile,
    ]
  )

  return (
    <EmbroideryOption.List>
      {validEmbroideryOptions.map((option, index) => {
        // Don't render embroidery options that don't have a valid form kind
        const formKind = getFormKind(option.kind)
        if (!formKind) return null

        const price = getFormattedPrice(option)
        const disabled =
          (option.kind === 'ICON_ONLY' && selectedOptions.isLogoSelected) ||
          (option.kind === 'LOGO_SETUP' && selectedOptions.isIconSelected) ||
          Boolean(defaultSelectedEmbroideryOptions?.[formKind])

        const checked =
          formKind === 'LOGO_SETUP'
            ? !!selectedEmbroideryOptions.ADD_LOGO
            : !!selectedEmbroideryOptions[formKind]

        // Render the portal logo preview image instead of the option button if defined as a default selected option
        const displayDefaultPortalLogoSelection =
          option.kind === 'LOGO_ONLY' && defaultSelectedEmbroideryOptions?.LOGO_ONLY

        // Indicate required portal options
        const required =
          isPortalColorSelected &&
          ((option.kind === 'LOGO_ONLY' && hasMandatoryLogoEmbroidery(productCategory)) ||
            (option.kind === 'TEXT_ONLY' && hasMandatoryTextEmbroidery(productCategory)))

        const error = formErrors[formKind]

        const label = `${checked ? edit : add} ${embroideryOptionTitles[option.kind]}${
          required ? ` (${validation.required})` : ''
        }`

        if (displayDefaultPortalLogoSelection)
          return (
            <StyledLogoSneakPeek
              key={label}
              data-testid='embroidery-option-logo-sneak-peek'
              backgroundColor={productColorHex}
              imgSrc={defaultSelectedEmbroideryOptions.LOGO_ONLY?.logoImageSource}
              logoName={defaultSelectedEmbroideryOptions.LOGO_ONLY?.logoName}
              logoPlacement={defaultSelectedEmbroideryOptions?.LOGO_ONLY?.logoPlacement}
            />
          )

        return (
          <EmbroideryOption.ListOption
            key={index}
            index={index}
            kind={formKind}
            handleClick={disabled ? () => null : setSelectedFormKind}
            label={label}
            disabled={disabled}
            error={
              error ? (
                <EmbroideryOption.Error id={`${option.kind}_formerror`} message={error} />
              ) : null
            }
            errorMessageId={`${option.kind}_formerror`}
          >
            <EmbroideryOption.Checkbox disabled={disabled} checked={checked}>
              <ListOptionText disabled={disabled} isInvalid={Boolean(error)}>
                {label}
                {newEmbroideryOptions.includes(option.kind) && (
                  <NewBadge disabled={disabled}>{strings.new}</NewBadge>
                )}
              </ListOptionText>
            </EmbroideryOption.Checkbox>
            {disabled ? (
              <DisabledMessage>{disabledText[option.kind]}</DisabledMessage>
            ) : (
              <EmbroideryOption.Price>
                <span aria-hidden='true'>
                  {hasTextPrefix(option.kind)} +{price}
                </span>
              </EmbroideryOption.Price>
            )}
          </EmbroideryOption.ListOption>
        )
      })}
    </EmbroideryOption.List>
  )
}

const ListOptionText = styled.div<{ disabled?: boolean; isInvalid?: boolean }>`
  color: ${o =>
    o.disabled
      ? o.theme.color.deprecated.stoneDark
      : o.isInvalid
      ? o.theme.color.deprecated.red.error
      : o.theme.color.deprecated.obsidian};
  letter-spacing: ${o => o.theme.typography.font.letterSpacing.wide};
  font-weight: ${o => o.theme.typography.font.weight[700]};
  line-height: ${o => o.theme.typography.font.lineHeight.loose};
`

const NewBadge = styled.span<{ disabled?: boolean }>`
  ${o => o.disabled && 'display: none;'}
  margin-left: ${o => o.theme.spacing(2.5)};
  font-size: 9px;
  border-radius: 4px;
  border: 1px solid ${o => o.theme.color.deprecated.blue.light};
  color: ${o => o.theme.color.deprecated.blue.light};
  background-color: #e9eefb;
  padding: ${o => o.theme.spacing(0.75)} ${o => o.theme.spacing(2)};
  letter-spacing: 1px;
  font-weight: ${o => o.theme.typography.font.weight[600]};
  line-height: 12px;
`

const StyledLogoSneakPeek = styled(LogoSneakPeek)`
  margin-top: ${o => o.theme.spacing(4)};
`

const DisabledMessage = styled(TypeStyleBodySmall)`
  font-style: italic;
  color: ${o => o.theme.color.deprecated.stoneDark};
  font-weight: ${o => o.theme.typography.font.weight[600]};
  line-height: 20px;
  letter-spacing: 0.6px;
  align-self: center;

  ${untilMd} {
    font-size: 10px;
    font-weight: ${o => o.theme.typography.font.weight[600]};
    line-height: 12px;
    letter-spacing: 0.4px;
  }
`
